import { z } from 'zod';

import { PatchAdminV1OrgStoresIdBodyWeightUnit } from '@nucleus-commerce/api-client/admin';
import { isMoney } from '@nucleus-commerce/core';

import { isCurrencyCode } from './currencies';
import { isTimeZone } from './timezones';

const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const LOCALE = /^[a-z]{2}(?:-[A-Z]{2})?$/;
const COUNTRY = /^[A-Z]{2}$/;
const TWITTER_HANDLE = /^@?\w{1,50}$/;
/** The backend's rule: a host name, no scheme, no path. */
export const PRIMARY_DOMAIN = /^[a-z0-9.-]+\.[a-z]{2,}$/;

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

/** The backend's rule for customer links: an http(s) URL with a `{path}` placeholder. */
export function isPathTemplate(value: string): boolean {
  if (!value.includes('{path}')) return false;
  try {
    const url = new URL(value.replace('{path}', 'path'));
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/** "AB" + 42 with padding 6 → "AB000042", the way the backend numbers orders. */
export function orderNumberExample(prefix: string, next: number, padding: number): string {
  return `${prefix}${String(next).padStart(padding, '0')}`;
}

const slug = z
  .string()
  .trim()
  .min(1, { error: 'stores:validation.required' })
  .max(120)
  .regex(SLUG, { error: 'stores:validation.slugFormat' });
const currency = z
  .string()
  .trim()
  .toUpperCase()
  .refine(isCurrencyCode, { error: 'stores:validation.currency' });
const locale = z.string().trim().regex(LOCALE, { error: 'stores:validation.locale' });
const country = z
  .string()
  .trim()
  .toUpperCase()
  .regex(COUNTRY, { error: 'stores:validation.country' });
const timezone = z
  .string()
  .trim()
  .min(1, { error: 'stores:validation.required' })
  .max(64)
  .refine(isTimeZone, { error: 'stores:validation.timezone' });
const optionalEmail = z
  .string()
  .trim()
  .refine((v) => v === '' || z.email().safeParse(v).success, { error: 'stores:validation.email' });
const money = z.custom<{ amount: number; currency: string }>(isMoney).nullable();

export const createStoreSchema = z.object({
  name: z.string().trim().min(1, { error: 'stores:validation.required' }).max(120),
  slug,
  defaultCurrency: currency,
  countryCode: country,
  timezone,
  defaultLocale: locale,
});

export type CreateStoreInput = z.input<typeof createStoreSchema>;
export type CreateStoreOutput = z.output<typeof createStoreSchema>;

/**
 * All blank means "no address"; otherwise the street and city are required and the country must
 * be a code, matching the backend's StoreAddress.
 */
const address = z
  .object({
    line1: z.string().trim().max(200, { error: 'stores:validation.addressLength' }),
    line2: z.string().trim().max(200, { error: 'stores:validation.addressLength' }),
    city: z.string().trim().max(120, { error: 'stores:validation.addressLength' }),
    region: z.string().trim().max(120, { error: 'stores:validation.addressLength' }),
    postalCode: z.string().trim().max(20, { error: 'stores:validation.postalCodeLength' }),
    countryCode: z.string().trim().toUpperCase(),
  })
  .superRefine((v, ctx) => {
    const filled = [v.line1, v.line2, v.city, v.region, v.postalCode].some((s) => s !== '');
    if (!filled) return;
    if (v.line1 === '') {
      ctx.addIssue({ code: 'custom', path: ['line1'], message: 'stores:validation.required' });
    }
    if (v.city === '') {
      ctx.addIssue({ code: 'custom', path: ['city'], message: 'stores:validation.required' });
    }
    if (!COUNTRY.test(v.countryCode)) {
      ctx.addIssue({ code: 'custom', path: ['countryCode'], message: 'stores:validation.country' });
    }
  });

export const storeSettingsSchema = z
  .object({
    name: z.string().trim().min(1, { error: 'stores:validation.required' }).max(120),
    slug,
    legalName: z.string().trim().max(200),
    contactEmail: optionalEmail,
    supportEmail: optionalEmail,
    supportPhone: z.string().trim().max(40),
    primaryDomain: z
      .string()
      .trim()
      .toLowerCase()
      .max(253)
      .refine((v) => v === '' || PRIMARY_DOMAIN.test(v), {
        error: 'stores:validation.primaryDomain',
      }),
    orderNumberPrefix: z.string().trim().max(8, { error: 'stores:validation.prefixLength' }),
    orderNumberPadding: z.coerce
      .number<string | number>()
      .int({ error: 'stores:validation.padding' })
      .min(0, { error: 'stores:validation.padding' })
      .max(8, { error: 'stores:validation.padding' }),
    nextOrderNumber: z.coerce
      .number<string | number>()
      .int({ error: 'stores:validation.nextOrderNumber' })
      .min(1, { error: 'stores:validation.nextOrderNumber' })
      .max(2_000_000_000, { error: 'stores:validation.nextOrderNumber' }),
    /** The counter as loaded; never sent, only the floor the new value may not go under. */
    currentNextOrderNumber: z.number().int(),
    defaultCurrency: currency,
    supportedCurrencies: z.array(currency).min(1, { error: 'stores:validation.required' }).max(20),
    defaultLocale: locale,
    supportedLocales: z.array(locale).min(1, { error: 'stores:validation.required' }).max(20),
    timezone,
    weightUnit: z.enum(PatchAdminV1OrgStoresIdBodyWeightUnit),
    countryCode: country,
    address,
    checkout: z.object({
      guestCheckout: z.boolean(),
      requirePhone: z.boolean(),
      termsUrl: z
        .string()
        .trim()
        .refine((v) => v === '' || isHttpsUrl(v), { error: 'stores:validation.httpsUrl' }),
      requireTermsAcceptance: z.boolean(),
      customerLinkUrlTemplate: z
        .string()
        .trim()
        .max(500, { error: 'stores:validation.templateLength' })
        .refine((v) => v === '' || isPathTemplate(v), { error: 'stores:validation.pathTemplate' }),
      codEnabled: z.boolean(),
      codMaxAmount: money,
      codFeeAmount: money,
    }),
    tracking: z.object({
      ga4Id: z.string().trim(),
      metaPixelId: z.string().trim(),
      tiktokPixelId: z.string().trim(),
      pinterestTagId: z.string().trim(),
      googleAdsId: z.string().trim(),
    }),
    seo: z.object({
      titleTemplate: z.string().trim().min(1, { error: 'stores:validation.required' }),
      defaultMetaDescription: z.string().trim(),
      robots: z.string().trim(),
      indexable: z.boolean(),
      organizationLegalName: z
        .string()
        .trim()
        .max(200, { error: 'stores:validation.legalNameLength' }),
      socialProfiles: z
        .array(z.string())
        .max(20, { error: 'stores:validation.socialProfilesMax' })
        .refine((urls) => urls.every(isHttpUrl), { error: 'stores:validation.socialProfileUrl' }),
      twitterHandle: z
        .string()
        .trim()
        .max(60, { error: 'stores:validation.twitterHandleLength' })
        .refine((v) => v === '' || TWITTER_HANDLE.test(v), {
          error: 'stores:validation.twitterHandle',
        }),
      logoMediaId: z.string().nullable(),
    }),
    crawlers: z.object({
      allowTraining: z.boolean(),
    }),
    documents: z.object({
      footer: z.string().max(2000, { error: 'stores:validation.footerLength' }),
      showPrices: z.boolean(),
    }),
  })
  .refine((v) => v.supportedCurrencies.includes(v.defaultCurrency), {
    path: ['supportedCurrencies'],
    error: 'stores:validation.supportedIncludesDefault',
  })
  .refine((v) => v.nextOrderNumber >= v.currentNextOrderNumber, {
    path: ['nextOrderNumber'],
    error: 'stores:validation.nextOrderNumberBackwards',
  })
  .refine((v) => v.supportedLocales.includes(v.defaultLocale), {
    path: ['supportedLocales'],
    error: 'stores:validation.supportedIncludesDefaultLocale',
  });

export type StoreSettingsInput = z.input<typeof storeSettingsSchema>;
export type StoreSettingsOutput = z.output<typeof storeSettingsSchema>;
